import { graphql } from "@repo/graphql-types/gql";
import { useFragment, type FragmentType } from "@repo/graphql-types";
import type { z } from "zod";
import type { UseFormReturn } from "react-hook-form";
import { CpsInput } from "corpus";
import { MinusCircle } from "@repo/icons";
import { useRouteContext } from "@tanstack/react-router";
import { FormField, FormItem, FormControl } from "@/components/form";
import type { oneLivFormBasicInfo } from "@/lib/form-schemas/oneliv-form-schema";
import { OneLivSpecialtySelectInput } from "@/components/oneliv/oneliv-specialty-select-input";
import { formatFirstLetterUppercase } from "@/lib/string";
import { ActionButtonText } from "@/components/action-button-text";
import { OneLivSubspecialtySelectInput } from "@/components/oneliv/oneliv-subspecialty-select-input";
import { CardRoot, CardContent, CardHeader, CardFooter } from "@/components/card";
import { OnelivAccordion } from "@/components/oneliv/oneliv-accordion";

export type OneLivFormBasicInfoValues = z.infer<typeof oneLivFormBasicInfo>;

export const OneLivSpecialtiesInputFragment = graphql(/* GraphQL */ `
  fragment OneLivSpecialtiesInputFragment on query_root {
    specialties: locus_especialidade(
      order_by: { especialista: asc }
      where: { codEspecialidade: { _is_null: false } }
    ) {
      id
      especialista
      conselhoProfissionalEspecialidades(
        where: { conselhoProfissional: { ativo: { _eq: true } } }
      ) {
        idConselhoProfissional
      }
      ...OneLivSpecialtySelectInputFragment
      ...OneLivSubspecialtySelectInputFragment
    }

    professionalCouncil: locus_conselho_profissional(where: { sigla: { _eq: "CRM" } }) {
      id
    }
  }
`);

interface OneLivSpecialtiesInputProps {
  form: UseFormReturn<OneLivFormBasicInfoValues>;
  data?: FragmentType<typeof OneLivSpecialtiesInputFragment> | null;
}

export const OneLivSpecialtiesInput = ({
  data,
  form,
}: OneLivSpecialtiesInputProps): JSX.Element => {
  const { flags } = useRouteContext({ strict: false });
  const showSubspecialtiesFields = flags["exibe-campos-de-subespecialidades"];

  const fragmentResult = useFragment(OneLivSpecialtiesInputFragment, data);

  const selectedProfessionalCouncil = form.watch("professionalCouncil");
  const specialtiesWatch = form.watch("specialties");

  const specialties = fragmentResult?.specialties;

  const addNewSubspecialty = (
    specialtyIndex: number,
    subspecialties: { subspecialty: number; rqe?: string }[],
  ): void => {
    form.setValue(`specialties.${specialtyIndex}.subspecialties`, [
      ...subspecialties,
      { subspecialty: 0, rqe: "" },
    ]);
  };

  const removeSubspecialty = (
    specialtyIndex: number,
    subspecialtyIndex: number,
    subspecialties: { subspecialty: number; rqe?: string }[],
  ): void => {
    const formValue = subspecialties.filter((_, i) => i !== subspecialtyIndex);
    form.setValue(`specialties.${specialtyIndex}.subspecialties`, formValue);
  };

  const handleSpecialtyChange = (specialtyIndex: number): void => {
    form.setValue(`specialties.${specialtyIndex}.subspecialties`, [
      { subspecialty: 0, rqe: "" },
    ]);
  };

  const getValidSpecialties = (): typeof specialties => {
    if (selectedProfessionalCouncil) {
      return specialties?.filter((specialty) =>
        specialty.conselhoProfissionalEspecialidades.some(
          (conselhoProfissionalEspecialidade) =>
            conselhoProfissionalEspecialidade.idConselhoProfissional ===
            selectedProfessionalCouncil,
        ),
      );
    }

    return specialties;
  };

  const validSpecialties = getValidSpecialties();

  const CRM_ID = fragmentResult?.professionalCouncil[0].id;

  const shouldDisplayRQEInputs =
    selectedProfessionalCouncil && selectedProfessionalCouncil === CRM_ID;

  const ordinalNumbers = ["primeira", "segunda", "terceira"];

  return (
    <>
      {specialtiesWatch.map((specialty, indexSpecialty) => {
        const specialtyWatch = form.watch(`specialties.${indexSpecialty}`);
        const isFirstSpecialty = indexSpecialty === 0;

        const specialistName = validSpecialties?.find(
          ({ id }) => id === specialtyWatch.specialty,
        )?.especialista;
        const specialtyRQEAccordionText = specialtyWatch.specialtyRQE
          ? ` (${specialtyWatch.specialtyRQE})`
          : "";
        const accordionTextWhenClosed = specialistName
          ? `${specialistName}${specialtyRQEAccordionText}`
          : "";

        return (
          <OnelivAccordion
            key={specialtiesWatch.indexOf(specialty)}
            defaultOpen={isFirstSpecialty}
            title={`${formatFirstLetterUppercase(indexSpecialty < ordinalNumbers.length ? ordinalNumbers[indexSpecialty] : "")} especialidade${isFirstSpecialty ? "" : " (opcional)"}`}
            hasDivider={false}
            defaultTextWhenClosed={accordionTextWhenClosed}
          >
            <div className="flex flex-col gap-6">
              <FormField
                control={form.control}
                name={`specialties.${indexSpecialty}.specialty`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <OneLivSpecialtySelectInput
                        required={isFirstSpecialty}
                        title={`Especialidade${isFirstSpecialty ? "" : " (opcional)"}`}
                        data={validSpecialties}
                        {...field}
                        onChange={(event) => {
                          field.onChange(event);
                          handleSpecialtyChange(indexSpecialty);
                        }}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {shouldDisplayRQEInputs &&
              (isFirstSpecialty || specialtyWatch.specialty !== 0) ? (
                <FormField
                  control={form.control}
                  name={`specialties.${indexSpecialty}.specialtyRQE`}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <CpsInput
                          title={`RQE da ${ordinalNumbers[indexSpecialty]} especialidade (opcional)`}
                          placeholder="Digite aqui"
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              ) : null}

              {showSubspecialtiesFields && specialtyWatch.specialty !== 0 ? (
                <>
                  {specialtyWatch.subspecialties?.map(
                    (subspecialty, indexSubspecialty) => (
                      <CardRoot
                        key={specialtyWatch.subspecialties?.indexOf(subspecialty)}
                      >
                        <CardHeader />
                        <CardContent className="py-0 flex flex-col gap-6">
                          <FormField
                            control={form.control}
                            name={`specialties.${indexSpecialty}.subspecialties.${indexSubspecialty}.subspecialty`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <OneLivSubspecialtySelectInput
                                    title={`Subespecialidade ${indexSubspecialty + 1} (opcional)`}
                                    data={validSpecialties}
                                    specialtyId={specialtyWatch.specialty}
                                    {...field}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          {shouldDisplayRQEInputs && subspecialty.subspecialty !== 0 ? (
                            <FormField
                              control={form.control}
                              name={`specialties.${indexSpecialty}.subspecialties.${indexSubspecialty}.rqe`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <CpsInput
                                      title="RQE da subespecialidade"
                                      placeholder="Digite aqui"
                                      {...field}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          ) : null}
                        </CardContent>

                        <CardFooter>
                          <ActionButtonText
                            Icon={MinusCircle}
                            color="danger"
                            className="whitespace-nowrap"
                            onClick={() =>
                              removeSubspecialty(
                                indexSpecialty,
                                indexSubspecialty,
                                specialtyWatch.subspecialties ?? [],
                              )
                            }
                          >
                            Remover
                          </ActionButtonText>
                        </CardFooter>
                      </CardRoot>
                    ),
                  )}

                  <ActionButtonText
                    onClick={() =>
                      addNewSubspecialty(
                        indexSpecialty,
                        specialtyWatch.subspecialties ?? [],
                      )
                    }
                  >
                    Adicionar nova subespecialidade
                  </ActionButtonText>
                </>
              ) : null}
            </div>
          </OnelivAccordion>
        );
      })}
    </>
  );
};
